import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";

interface BlogSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

interface BlogData {
  seoTitle: string;
  metaDescription: string;
  h1: string;
  imageAlt: string;
  focusKeywords: string;
  intro: string;
  sections: BlogSection[];
  ctaText: string;
  publishedDate: string;
  category: string;
}

const blogPosts: Record<string, BlogData> = {
  "hotel-laundry-outsourcing-london": {
    seoTitle:
      "Why London Hotels Are Outsourcing Guest & Staff Laundry | Xcel Dry Cleaners",
    metaDescription:
      "Discover why hotels are outsourcing guest and staff laundry to specialist cleaners — same-day collection, consistent quality and lower costs.",
    h1: "Why More Hotels Are Outsourcing Guest & Staff Laundry",
    imageAlt:
      "Freshly pressed hotel linen and staff uniforms ready for same-day delivery",
    focusKeywords:
      "hotel laundry service, hotel laundry outsourcing, guest laundry service, hotel uniform cleaning, commercial laundry for hotels",
    intro:
      "Running an in-house laundry operation is one of the most overlooked costs in hotel management. Between machinery, detergent, water, energy and dedicated staff hours, many hotels are discovering that outsourcing guest and staff laundry to a specialist actually costs less and delivers a more consistent result.",
    sections: [
      {
        heading: "The Hidden Cost of In-House Laundry",
        paragraphs: [
          "Commercial washing machines and presses require significant upfront investment and ongoing maintenance. Add in utility costs and the wages of staff dedicated purely to laundry, and many mid-sized hotels find the numbers simply don't add up, especially when occupancy fluctuates through the week or across seasons.",
        ],
      },
      {
        heading: "Consistency Guests Notice",
        paragraphs: [
          "Guests judge a hotel on small details, and freshly pressed shirts or perfectly finished linen are exactly the kind of detail that ends up in a five-star review. A specialist laundry partner applies the same quality checks to every order, which is difficult to replicate with an in-house team juggling multiple responsibilities.",
        ],
      },
      {
        heading: "Same-Day Turnaround Keeps Operations Moving",
        paragraphs: [
          "One of the biggest advantages of outsourcing is speed. A same-day collection and delivery model, laundry collected in the morning and returned that evening, means hotels never need to hold large reserves of spare uniforms, and guest requests can be fulfilled without delay, even during busy periods.",
        ],
      },
      {
        heading: "Staff Uniforms Matter Too",
        paragraphs: [
          "It isn't just guest laundry that benefits. Housekeeping, front-of-house and food and beverage teams all rely on clean, presentable uniforms for every shift. Outsourcing uniform laundering removes a daily logistical burden from hotel management and ensures every team member starts their shift looking the part.",
        ],
      },
      {
        heading: "What to Look for in a Laundry Partner",
        bullets: [
          "A genuine same-day collection and delivery service, not just next-day",
          "Experience working with hotels and hospitality businesses specifically",
          "Clear communication and a dedicated point of contact",
          "The ability to flex capacity up during busy periods",
          "A proven track record with other local properties",
        ],
        paragraphs: [
          "Xcel Dry Cleaners already provides guest and staff laundry services to hotels around ExCeL London, with same-day collection every morning and delivery back that evening.",
        ],
      },
    ],
    ctaText:
      "Want to move your hotel's laundry off-site without losing speed or quality? Get in touch with Xcel Dry Cleaners to set up a same-day collection schedule.",
    publishedDate: "2025-07-01",
    category: "Hotel Laundry",
  },
  "professional-garment-care-guide": {
    seoTitle: "Professional Garment Care Guide | Xcel Dry Cleaners London",
    metaDescription:
      "Learn how professional dry cleaning and garment care extend the life of suits, shirts and delicate fabrics. Expert tips from Xcel Dry Cleaners London.",
    h1: "The Ultimate Guide to Professional Garment Care",
    imageAlt: "Tailor pressing a suit jacket during professional dry cleaning",
    focusKeywords:
      "professional garment care, dry cleaning tips, suit dry cleaning, delicate fabric cleaning",
    intro:
      "A well-made garment is an investment, and how it's cleaned has a direct impact on how long it lasts. Professional garment care goes far beyond a standard wash cycle — it's a combination of fabric knowledge, the right cleaning method and careful finishing that keeps clothes looking sharp for years rather than months.",
    sections: [
      {
        heading: "Why Dry Cleaning Isn't Just for Special Occasions",
        paragraphs: [
          "Many people only think of dry cleaning for suits or eveningwear, but everyday items like shirts, trousers, coats and workwear benefit just as much. Regular professional cleaning removes ground-in dirt and oils that home washing can miss, which helps fabric keep its structure and colour for longer.",
        ],
      },
      {
        heading: "Understanding Different Fabrics",
        paragraphs: [
          "Wool, silk, linen and blended fabrics all react differently to heat, water and agitation. A professional cleaner assesses each garment individually, choosing a cleaning method suited to the fibre rather than applying a one-size-fits-all cycle. This is especially important for tailored pieces, where the wrong process can shrink or misshape the garment.",
        ],
      },
      {
        heading: "The Value of Proper Pressing and Finishing",
        paragraphs: [
          "Cleaning is only half the job. Correct pressing restores a garment's original shape, sharpens creases and collars, and removes the kind of wrinkles that home ironing can struggle with. This finishing step is often what makes the biggest visible difference between a home wash and a professional clean.",
        ],
      },
      {
        heading: "Simple Habits That Extend Garment Life",
        bullets: [
          "Address stains as soon as possible rather than letting them set",
          "Use proper hangers for suits and coats to help them keep their shape",
          "Avoid over-cleaning items that don't need it, excessive cleaning can wear fabric down",
          "Store seasonal garments clean, since stains and body oils attract pests over time",
          "Choose a cleaner who tailors the process to the fabric, not a generic cycle",
        ],
      },
      {
        heading: "When to Call in the Experts",
        paragraphs: [
          "Delicate fabrics, tailored suits, wedding wear and anything with stubborn or unknown stains are best left to professionals. Xcel Dry Cleaners assesses every garment individually, using the right process for the fabric to protect its colour, shape and lifespan — the same standard of care applied to the hotel guest laundry Xcel handles every day.",
        ],
      },
    ],
    ctaText:
      "Trust your wardrobe to specialists who treat every fabric differently. Book a collection with Xcel Dry Cleaners today.",
    publishedDate: "2025-07-01",
    category: "Garment Care",
  },
  "commercial-cleaning-services-london": {
    seoTitle:
      "Commercial Laundry & Dry Cleaning Services for Businesses | Xcel Dry Cleaners",
    metaDescription:
      "Xcel Dry Cleaners provides commercial laundry and dry cleaning for hotels, offices and businesses across London, with reliable same-day collection.",
    h1: "Commercial Laundry & Dry Cleaning Services for Businesses",
    imageAlt:
      "Commercial laundry staff sorting business linen and uniforms for delivery",
    focusKeywords:
      "commercial laundry, business dry cleaning service, uniform cleaning service, commercial cleaning services",
    intro:
      "From hotels and serviced apartments to offices, restaurants and healthcare settings, businesses increasingly rely on a professional partner to handle laundry and dry cleaning at scale. A commercial cleaning service takes on a task that would otherwise eat into staff time, space and budget, and does it with a consistency that's hard to match in-house.",
    sections: [
      {
        heading: "Who Benefits from Commercial Laundry Services",
        paragraphs: [
          "Hotels needing guest and staff laundry, offices with corporate uniforms, restaurants managing tablecloths and staff whites, and healthcare or hospitality businesses with strict hygiene standards all benefit from outsourcing to a specialist. Each sector has different volume and turnaround needs, and a good commercial laundry partner adapts the service accordingly.",
        ],
      },
      {
        heading: "What a Commercial Service Should Offer",
        bullets: [
          "Scheduled collection and delivery that fits around business hours",
          "Same-day turnaround for time-sensitive items",
          "Consistent quality control across every order, regardless of volume",
          "The flexibility to scale up during busy periods or events",
          "A single, reliable point of contact for scheduling and queries",
        ],
      },
      {
        heading: "Case in Point: Hotel Partnerships",
        paragraphs: [
          "Xcel Dry Cleaners already provides commercial guest and staff laundry services to hotels around ExCeL London. Items are collected each morning and delivered back the same evening, giving each hotel a dependable laundry operation without the overhead of managing it in-house.",
        ],
      },
      {
        heading: "The Bottom Line",
        paragraphs: [
          "Outsourcing commercial laundry isn't just about convenience — it's about freeing up staff time, reducing overhead and guaranteeing a standard of quality that reflects well on the business. Whether it's hotel linen, staff uniforms or everyday business laundry, a same-day commercial partner keeps operations running smoothly.",
        ],
      },
    ],
    ctaText:
      "Ready to simplify your business laundry? Contact Xcel Dry Cleaners to set up a commercial collection and delivery schedule.",
    publishedDate: "2025-07-01",
    category: "Commercial Laundry",
  },
};

function BlogPage() {
  const { slug } = useParams<{ slug: string }>();
  const data = slug ? blogPosts[slug] : undefined;

  useEffect(() => {
    if (!data) return;
    document.title = data.seoTitle;
    document.getElementById("title")?.setAttribute("content", data.seoTitle);
    document
      .getElementById("description")
      ?.setAttribute("content", data.metaDescription);
  }, [data]);

  if (!data) return <Navigate to="/404" replace />;

  const otherPosts = Object.entries(blogPosts)
    .filter(([s]) => s !== slug)
    .map(([s, d]) => ({ slug: s, h1: d.h1 }));

  return (
    <>
      <div className="xcel-page-hero">
        <div className="container">
          <nav className="xcel-breadcrumb" aria-label="breadcrumb">
            <a href="/">Home</a>
            <span aria-hidden="true"> / </span>
            <a href="/blog">Blog</a>
            <span aria-hidden="true"> / </span>
            <span aria-current="page">{data.category}</span>
          </nav>
          <h1>{data.h1}</h1>
          <p className="xcel-page-hero-meta">
            {data.category} &nbsp;·&nbsp;{" "}
            <time dateTime={data.publishedDate}>
              {new Date(data.publishedDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </time>
          </p>
        </div>
      </div>

      <div className="xcel-blog-article">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <p className="xcel-blog-intro">{data.intro}</p>

              {data.sections.map((section, i) => (
                <section key={i} className="xcel-blog-section">
                  <h2 className="xcel-cs-section-title">{section.heading}</h2>
                  {section.paragraphs?.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                  {section.bullets && (
                    <ul className="xcel-why-list">
                      {section.bullets.map((b, j) => (
                        <li key={j}>{b}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <div className="xcel-cta-banner">
                <h3>{data.ctaText}</h3>
                <Link to="/Contactus" className="xcel-cta-btn">
                  Contact Us
                </Link>
              </div>
            </div>

            <aside className="col-lg-4">
              <div className="xcel-cs-sidebar">
                <h3 className="xcel-cs-sidebar-title">More Articles</h3>
                <ul className="xcel-cs-related-list">
                  {otherPosts.map(({ slug: s, h1 }) => (
                    <li key={s}>
                      <Link to={`/blog/${s}`}>{h1}</Link>
                    </li>
                  ))}
                </ul>

                <div className="xcel-cs-sidebar-info mt-4">
                  <h4>Our Hotel Partnerships</h4>
                  <ul>
                    <li>
                      <Link to="/case-studies/novotel-london-excel-laundry-partnership">
                        Novotel London Excel
                      </Link>
                    </li>
                    <li>
                      <Link to="/case-studies/crowne-plaza-london-docklands-laundry-partnership">
                        Crowne Plaza London Docklands
                      </Link>
                    </li>
                    <li>
                      <Link to="/case-studies/ibis-london-excel-docklands-laundry-partnership">
                        ibis London Excel Docklands
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="xcel-cs-sidebar-cta">
                  <p>Ready to book a collection?</p>
                  <Link to="/productLists" className="xcel-sidebar-btn">
                    Book Now
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}

export default BlogPage;
